import React from 'react';
import {errorMessageFromServerError} from "../Helpers/Helpers";

export default class InfiniteScrollManager extends React.Component{
  
  // Instance Variables
  
  list = [];
  limit = 10;
  loading = false;
  loadedAll = false;
  knownTotal = null;
  shouldLoadOnSuccess = false;
  requestVersion = 0;
  activeRequestVersion = null;
  disposed = false;
  
  // Getters
  
  getList(){
    return this.list;
  }
  
  isLoading(){
    return this.loading;
  }
  
  totalCount(){
    return this.list.length;
  }

  knownTotalCount(){
    return this.knownTotal;
  }
  
  hasLoadedAll(){
    return this.loadedAll;
  }
  
  // Setters
  
  setList(aList, aTotal = null){
    this.requestVersion += 1;
    this.activeRequestVersion = null;
    this.list = Array.isArray(aList) ? aList : [];
    const hasTotal = aTotal !== null && aTotal !== undefined && aTotal !== '';
    this.knownTotal = hasTotal && Number.isFinite(Number(aTotal)) ? Number(aTotal) : null;
    this.loading = false;
    this.loadedAll = this.knownTotal !== null && this.list.length >= this.knownTotal;
    this.shouldLoadOnSuccess = false;
  }

  dispose(){
    this.disposed = true;
    this.requestVersion += 1;
    this.activeRequestVersion = null;
    this.loading = false;
    this.shouldLoadOnSuccess = false;
  }
  
  // Init
  
  constructor(props){
    super(props);
    
    if(props.limit && props.limit > 0){
      this.limit = props.limit;
    }
    this.fetch = this.fetch.bind(this);
    this.getList = this.getList.bind(this);
    this.setList = this.setList.bind(this);
    this.dispose = this.dispose.bind(this);
    
    if(props.fetchImmediately){
      this.fetch(true);
    }
  }

  componentWillUnmount(){
    this.dispose();
  }
  
  // Methods
  
  fetch(aReload = false){
    return this.fetchAndReloadWithLimit(aReload, this.limit);
  }
  
  fetchWithLimit(aLimit){
    return this.fetchAndReloadWithLimit(false, aLimit);
  }
  
  reloadWithLimit(aLimit){
    return this.fetchAndReloadWithLimit(true, aLimit);
  }
  
  fetchAndReloadWithLimit(aReload, aLimit){
    const {fail, success, ignoreSequentialLoadRequests = false} = this.props;
    let limit = this.limit;
    
    if(aLimit > 0){
      limit = aLimit;
    }
    this.limit = limit;

    if(this.disposed){
      return Promise.resolve(null);
    }

    if(aReload){
      // Start replacement queries immediately. The previous request may never
      // settle, so waiting for it would indefinitely block a newer search,
      // sort, or filter. Its generation can no longer mutate this manager.
      this.requestVersion += 1;
      this.list = [];
      this.knownTotal = null;
      this.loadedAll = false;
      this.shouldLoadOnSuccess = false;
    }
    else if(this.isLoading()){
      if(!ignoreSequentialLoadRequests){
        this.shouldLoadOnSuccess = (this.hasLoadedAll() === false);
      }
      return Promise.resolve(null);
    }
    else if(this.hasLoadedAll()){
      return Promise.resolve(null);
    }

    const requestVersion = this.requestVersion;
    const offset = this.getList().length;
    this.loading = true;
    this.activeRequestVersion = requestVersion;

    let request;
    try{
      request = Promise.resolve(this.props.getItems(offset, limit));
    }
    catch(error){
      request = Promise.reject(error);
    }

    return request.then((newResult) => {
      if(this.disposed){
        return null;
      }
      if(requestVersion !== this.requestVersion ||
        this.activeRequestVersion !== requestVersion){
        return null;
      }
      this.loading = false;
      this.activeRequestVersion = null;

      if(newResult){
        const objects = Array.isArray(newResult.objects) ? newResult.objects : [];
        this.list.push(...objects);

        const hasTotal = newResult.total !== null && newResult.total !== undefined && newResult.total !== '';
        const parsedTotal = Number(newResult.total);
        if(hasTotal && Number.isFinite(parsedTotal) && parsedTotal >= 0){
          this.knownTotal = parsedTotal;
          this.loadedAll = objects.length === 0 || this.list.length >= parsedTotal;
        }
        else{
          this.knownTotal = null;
          this.loadedAll = objects.length < limit;
        }
      }
      else{
        this.knownTotal = this.list.length;
        this.loadedAll = true;
      }
      if(success){
        success(newResult);
      }
      return this.flushPendingRequest();
    }, (newError) => {
      if(this.disposed){
        return null;
      }
      if(requestVersion !== this.requestVersion ||
        this.activeRequestVersion !== requestVersion){
        return null;
      }
      this.loading = false;
      this.activeRequestVersion = null;
      this.shouldLoadOnSuccess = false;
      if(fail){
        fail(errorMessageFromServerError(newError));
      }
      return null;
    });
  }

  flushPendingRequest(){
    if(this.disposed){
      return Promise.resolve(null);
    }
    if(this.shouldLoadOnSuccess){
      this.shouldLoadOnSuccess = false;
      if(!this.hasLoadedAll()){
        return this.fetchAndReloadWithLimit(false, this.limit);
      }
    }
    return Promise.resolve(null);
  }
  
  insertItem(aItem, aIndex = null){
    if(aIndex >= 0 && aIndex < this.totalCount()){
      this.list.splice(aIndex, 0, aItem);
    }
    else{
      this.list.push(aItem);
    }
    if(this.knownTotal !== null){
      this.knownTotal += 1;
    }
  }
  
  removeItemMatchingProperty(aItem, aProperty){
    if(this.list.length > 0){
      if(this.list[0][aProperty] !== undefined){
        for(let index = 0; index < this.list.length; index += 1){
          if(this.list[index][aProperty] === aItem[aProperty]){
            this.removeItemAtIndex(index);
            break;
          }
        }
      }
      else{
        console.log('InfiniteScrollManager (removeItemMatchingProperty): Property:', aProperty, 'does not exist on items in the list:', this.list[0]);
      }
    }
  }
  
  removeItem(aItem){
    let index = this.getIndexOfItem(aItem);
    
    if(index >= 0 && index < this.totalCount()){
      this.removeItemAtIndex(index);
    }
    else{
      console.log('InfiniteScrollManager (removeItem): Cannot remove item:', aItem, 'as it does not belong to the list!');
    }
  }
  
  removeItemAtIndex(aIndex){
    let returnValue = null;
    
    if(aIndex < this.totalCount()){
      let item = this.list.splice(aIndex, 1);
      returnValue = item[0];
      if(this.knownTotal !== null){
        this.knownTotal = Math.max(0, this.knownTotal - 1);
      }
    }
    else{
      console.log('InfiniteScrollManager (removeItemAtIndex): Cannot remove item at index:', aIndex, 'as the list only has', this.totalCount(), 'elements!');
    }
    return returnValue;
  }
  
  replaceItem(aItem){
    this.replaceItemMatchingProperty(aItem, 'id');
  }
  
  replaceItemAtIndex(aItem, aIndex){
    if(aIndex < this.totalCount()){
      this.list[aIndex] = aItem;
    }
    else{
      console.log('InfiniteScrollManager (replaceItemAtIndex): Cannot replace item at index:', aIndex, 'as the list only has', this.totalCount(), 'elements!');
    }
  }
  
  replaceItemMatchingProperty(aItem, aProperty){
    if(this.list.length > 0){
      if(this.list[0][aProperty] !== undefined){
        for(let index = 0; index < this.list.length; index += 1){
          if(this.list[index][aProperty] === aItem[aProperty]){
            this.replaceItemAtIndex(aItem, index);
            break;
          }
        }
      }
      else{
        console.log('InfiniteScrollManager (replaceItemMatchingProperty): Property:', aProperty, 'does not exist on items in the list:', this.list[0]);
      }
    }
  }
  
  getIndexOfItem(aItem){
    return this.list.findIndex((item) => item === aItem);
  }
  
  // Render
  
  render(){
    return null
  }
}
